import { useEffect, useState } from 'react';

import getConfig from 'next/config';

import mux from 'mux-embed';

import {
  useSessionPresentation,
  resolveTotalTime,
  resolveSeekPosition,
  resolveOverallTime,
} from 'hooks/use-session-presentation';
import { MuxDataOptions } from 'typings/videojs-mux';

const { publicRuntimeConfig } = getConfig();

/**
 * Tracks the time user spent watching video. Tracking events are fired
 * whenever a pause is pressed, when the video is loading or when the user
 * closes the window.
 * Instead of using the internal video time `event.target.currentTime`, it
 * uses browser time `Date.now()` because utilizing the former would require
 * to also track `seek` events.
 */
export const useVideoTracking = (
  videoElement: HTMLVideoElement | null | undefined,
  logger: (props: { watchedFor: number }) => void,
) => {
  useEffect(() => {
    const videoEl = videoElement;

    let lastPlayStart: null | number = null;

    const onPause = () => {
      if (lastPlayStart === null) return;

      // seconds
      const watchedFor = (Date.now() - lastPlayStart) / 1000;
      lastPlayStart = null;

      logger({
        watchedFor,
      });
    };

    const onPlay = () => {
      lastPlayStart = Date.now();
    };

    // The first play event (click on the video overlay) is lost, probably
    // due to a rerender, so we use `timeupdate` event to detect playing video.
    const onUpdated = () => {
      if (lastPlayStart === null) lastPlayStart = Date.now();
    };

    if (videoEl) videoEl.addEventListener('timeupdate', onUpdated);
    if (videoEl) videoEl.addEventListener('play', onPlay);
    if (videoEl) videoEl.addEventListener('pause', onPause);
    if (videoEl) videoEl.addEventListener('waiting', onPause);
    window.addEventListener('beforeunload', onPause);

    return () => {
      onPause();
      if (videoEl) videoEl.removeEventListener('timeupdate', onUpdated);
      if (videoEl) videoEl.removeEventListener('play', onPlay);
      if (videoEl) videoEl.removeEventListener('pause', onPause);
      if (videoEl) videoEl.removeEventListener('waiting', onPause);
      window.removeEventListener('beforeunload', onPause);
    };
  }, [videoElement, logger]);
};

export type VideoPlayerWrapperProps = {
  id: string;
  videoSession: MorressierVideoSession | null;
  archiveStreamUrl?: string;
};

export const useVideoPlayerWrapper = ({
  id,
  videoSession,
  archiveStreamUrl,
}: VideoPlayerWrapperProps) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFullPage, setIsFullPage] = useState<boolean>(false);

  const [volume, setVolume] = useState<number>(1);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState<number>(0);

  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  const [currentSectionTime, setCurrentSectionTime] = useState<number>(0);
  const [isEnded, setIsEnded] = useState(false);

  const { sections } = useSessionPresentation(id, videoSession, archiveStreamUrl);

  useEffect(() => {
    if (sections) {
      const total = resolveTotalTime(sections)();
      setTotalTime(total);
    }
  }, [sections]);

  useEffect(() => {
    setIsEnded(false);
  }, [id]);

  const handleSeek = seekProgress => {
    const { index, chunkTime } = resolveSeekPosition(sections)(seekProgress);
    setCurrentSectionIndex(index);
    setCurrentSectionTime(chunkTime);
  };

  const handleRePlay = () => {
    setIsEnded(false);
    setCurrentPlaybackTime(0);
    setCurrentSectionIndex(0);
    setCurrentSectionTime(0);
    setIsPlaying(true);
  };

  const handleVideoTimeUpdate = videoTime => {
    try {
      const { playbackTime, isComplete } = resolveOverallTime(sections)(
        currentSectionIndex,
        videoTime,
      );

      if (isEnded) {
        setIsPlaying(false);
        setCurrentSectionTime(0);
        setCurrentPlaybackTime(0);
        setCurrentSectionIndex(0);
      } else {
        setCurrentPlaybackTime(playbackTime);
        if (isComplete) {
          if (currentSectionIndex < sections.length - 1) {
            setCurrentSectionIndex(currentSectionIndex + 1);
          } else {
            setIsEnded(true);
          }
        }
      }
    } catch (err) {
      // reset to first section
      setCurrentSectionIndex(0);
      setCurrentSectionTime(0);
      setIsPlaying(false);
    }
  };

  const handlePlayChange = () => {
    setIsPlaying(!isPlaying);
  };

  const sectionData = sections[currentSectionIndex];

  return {
    isDebug: false,
    isPlaying,
    src: sectionData?.video_url || '',
    fileType: sectionData?.is_archived && !archiveStreamUrl ? 'mp4' : '',
    start: sectionData?.start || 0,
    volume,
    playTime: currentSectionTime,
    onTimeUpdate: handleVideoTimeUpdate,
    onSeek: handleSeek,
    setIsFullPage,
    isFullPage,
    onPlayChange: handlePlayChange,
    currentTime: currentPlaybackTime,
    setVolume,
    totalTime,
    videoSession,
    isEnded,
    handleRePlay,
  };
};

export const useInitMux = (
  videoElement: HTMLVideoElement | null | undefined,
  muxMetaData: Omit<
    MuxDataOptions,
    'video_duration' | 'player_init_time' | 'env_key' | 'video_encoding_variant'
  >,
) => {
  // Mux init
  useEffect(() => {
    if (videoElement) {
      const initTime = Date.now();
      mux.monitor(videoElement, {
        debug: false,
        data: {
          env_key: publicRuntimeConfig.MUX_ENV_KEY,
          // Metadata
          player_name: muxMetaData.player_name,
          viewer_user_id: muxMetaData.viewer_user_id,
          video_id: muxMetaData.video_id,
          video_title: muxMetaData.video_title,
          video_duration: videoElement.duration,
          player_init_time: initTime,
        },
      });
    }
  }, [videoElement, muxMetaData]);
};

export const useVideoTime = (
  videoElement: HTMLVideoElement | null | undefined,
  callback: (time) => void,
) => {
  const videoEl = videoElement;
  useEffect(() => {
    const onTimeUpdate = evt => {
      callback(evt.target.currentTime);
    };

    if (videoEl) videoEl.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      if (videoEl) videoEl.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [videoEl, callback]);
};
